import { FC, useReducer, useState } from 'react';
import { TextInput } from '@carbon/react';
import { useStore } from '@tanstack/react-store';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { DatePicker } from 'components/date-picker';

import { usePackageWizardCtx } from '../context';
import { EntityPicker } from '../entity-picker';
import { makeDateClearHandler, makeDateFieldHandler } from '../utils';
import { Step2AssetsTable } from './step2-assets-table';
import { Step2SlotDetailsTable } from './step2-slot-details-table';
import { Step2SlotsTable } from './step2-slots-table';
import styles from './step2-review-panel.module.scss';

/**
 * Step 2 left pane. "Review Package" heading + metadata form (acquiring
 * entity, package name, 3 dates) + review accordions. Today the Assets,
 * Properties, and Property Allocations accordions are wired (read-only views
 * over the draft's selected rows); Package Contents and the Well Type /
 * Formation breakdowns under Properties follow once their data sources land.
 *
 * @return {JSX.Element} Step 2 review panel
 */
export const Step2ReviewPanel: FC = () => {
  const { form } = usePackageWizardCtx();

  const packageName = useStore(form.store, (s) => s.values.packageName);
  const acquiringEntities = useStore(
    form.store,
    (s) => s.values.acquiringEntities
  );
  const effectiveDate = useStore(form.store, (s) => s.values.effectiveDate);
  const psaDate = useStore(form.store, (s) => s.values.psaDate);
  const closeDate = useStore(form.store, (s) => s.values.closeDate);
  // Bumped when a typed date is ignored, so the fields re-mount showing the
  // dates kept (pickedDate).
  const [datesKey, showKeptDates] = useReducer((key: number) => key + 1, 0);
  const draftId = useStore(form.store, (s) => s.values.draftId);
  const onAssetList = useStore(
    form.store,
    (s) => s.values.sourceType === 'assetList'
  );

  const [assetsOpen, setAssetsOpen] = useState(true);
  const [slotsOpen, setSlotsOpen] = useState(true);
  const [slotAllocationsOpen, setSlotAllocationsOpen] = useState(true);

  return (
    <div className={styles.panel}>
      <div className={styles.scroll}>
        <h2 className={styles.title}>Review Package</h2>

        <div className={styles.metadata}>
          <EntityPicker
            value={acquiringEntities}
            onChange={(entity) =>
              form.setFieldValue('acquiringEntities', [entity])
            }
          />

          <TextInput
            id="package-name"
            labelText="Package Name *"
            placeholder="Enter a package name"
            light
            required
            value={packageName}
            onChange={({ target: { value } }) =>
              form.setFieldValue('packageName', value)
            }
          />

          <div className={styles.dateRow}>
            <div className={styles.dateField}>
              <DatePicker
                key={`effective-${datesKey}`}
                id="step2-effective-date"
                labelText="Effective Date"
                light
                value={effectiveDate ?? undefined}
                onChange={makeDateFieldHandler(
                  form,
                  'effectiveDate',
                  effectiveDate,
                  showKeptDates
                )}
                onInputChange={makeDateClearHandler(
                  form,
                  'effectiveDate',
                  effectiveDate
                )}
              />
            </div>
            <div className={styles.dateField}>
              <DatePicker
                key={`psa-${datesKey}`}
                id="step2-psa-date"
                labelText="PSA Date"
                light
                value={psaDate ?? undefined}
                onChange={makeDateFieldHandler(
                  form,
                  'psaDate',
                  psaDate,
                  showKeptDates
                )}
                onInputChange={makeDateClearHandler(form, 'psaDate', psaDate)}
              />
            </div>
            <div className={styles.dateField}>
              <DatePicker
                key={`close-${datesKey}`}
                id="step2-close-date"
                labelText="Close Date"
                light
                value={closeDate ?? undefined}
                onChange={makeDateFieldHandler(
                  form,
                  'closeDate',
                  closeDate,
                  showKeptDates
                )}
                onInputChange={makeDateClearHandler(
                  form,
                  'closeDate',
                  closeDate
                )}
              />
            </div>
          </div>
        </div>

        {draftId !== null && (
          <>
            <Accordion
              className={styles.accordionItem}
              expanded={assetsOpen}
              onClick={setAssetsOpen}
            >
              <AccordionSummary>Assets</AccordionSummary>
              <AccordionDetails className={styles.accordionDetailsOverride}>
                <div className={styles.accordionTable}>
                  <Step2AssetsTable
                    draftId={draftId}
                    allEntities={onAssetList}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion
              className={styles.accordionItem}
              expanded={slotsOpen}
              onClick={setSlotsOpen}
            >
              <AccordionSummary>Properties</AccordionSummary>
              <AccordionDetails className={styles.accordionDetailsOverride}>
                <div className={styles.accordionTable}>
                  <Step2SlotDetailsTable draftId={draftId} />
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion
              className={styles.accordionItem}
              expanded={slotAllocationsOpen}
              onClick={setSlotAllocationsOpen}
            >
              <AccordionSummary>Property Allocations</AccordionSummary>
              <AccordionDetails className={styles.accordionDetailsOverride}>
                <div className={styles.accordionTable}>
                  <Step2SlotsTable draftId={draftId} />
                </div>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </div>
    </div>
  );
};
